"use client";

import { useSelector } from "@xstate/store-react";
/**
 * The session, and the only file in this repository that knows the vendor's
 * name. Everything else — every part, every app — works against `useCall()`.
 *
 * IT RENDERS THE STAGE ITSELF, and no variant may. That is the single
 * load-bearing rule of this whole arrangement. The obvious composition,
 * `{minimised ? <PipCall/> : <FullCall/>}` with each rendering its own stage,
 * puts two DIFFERENT component types at one position in the tree: React
 * unmounts the subtree, the `<audio>` element goes with it, and
 * `bindAudioElement` is left pointing at a detached node. Remote audio dies
 * while the call still looks connected — nothing throws, nothing logs, and the
 * clinician's first clue is silence. So the stage hangs off the provider, above
 * `children`, at a position that cannot change; the variants are chrome that
 * positions itself over it and owns nothing.
 *
 * `Call.Stage` is deliberately NOT on the exported namespace. The brief called
 * for a guard that makes "one stage, owned by the provider" checkable; not
 * exporting it makes composing it into a variant unrepresentable rather than
 * merely caught, and the guard stays anyway for the day somebody re-exports it.
 *
 * THE VENDOR ALSO READS NODE'S `global`, from its CSP monitor, which a browser
 * does not have. Every consuming app needs `define: { global: "globalThis" }`
 * in its vite config; the symptom is a ReferenceError thrown from
 * `new DefaultMeetingSession`, i.e. only ever at the moment somebody joins a
 * call, never at build or typecheck time. `vite-define.test.ts` next door makes
 * that a red CI run instead.
 */
import {
  ConsoleLogger,
  DefaultDeviceController,
  DefaultMeetingSession,
  LogLevel,
  MeetingSessionConfiguration,
} from "amazon-chime-sdk-js";
import { type ReactNode, useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { JoinCredential, RoomKey } from "../room";
import {
  type CallRole,
  callStore,
  enterCall,
  expandCall,
  leaveCall,
  minimiseCall,
  toggleCallAside,
} from "./call-store";
import { CallContext, type CallContextValue, type CallStatus, type CallTile, type WaitingEntry } from "./context";
import { createCallDiagnostics } from "./diagnostics";
import { shouldExpandForPresence } from "./first-remote-presence";
import { useSurfaceGeometry } from "./geometry";
import { createMediaReadiness, type MediaCapture, publishPreparedCamera } from "./media-readiness";
import { type CallParticipant, updateRoster } from "./participant-roster";
import { CallStage } from "./stage";
import { startWithMicrophone } from "./start-with-microphone";

/**
 * THE ONE CAST. `JoinCredential.payload` is `unknown` from the port outwards so
 * that nothing between the driver and here can be tempted to read it; this is
 * the browser adapter the port's doc comment names as the single exception.
 */
interface VendorJoinPayload {
  Meeting: unknown;
  Attendee: { AttendeeId?: string };
}

/** Loud enough to diagnose a failed join, quiet enough not to bury the console. */
const LOGGER_NAME = "conferencing";

const nameOf = (externalUserId: string | null | undefined) => externalUserId?.split("#")[0] ?? "Guest";

export interface ConferencingProviderProps {
  children: ReactNode;
  /** The room this surface is for. Also what the store keys the active call by. */
  roomKey: RoomKey;
  /**
   * `role` is the word everywhere else — `ConferencingPort.issueCredential`,
   * `join-visit-room.query.ts`, the store's `ActiveCall.role`, the `CallRole`
   * type this is. It is qualified HERE and only here because a JSX attribute
   * spelled `role` is the ARIA attribute as far as a reader and a linter are
   * concerned, and Biome's `a11y/useValidAriaRole` rejects `role="host"` on
   * sight. One concept, one word; the prefix is a JSX escape, not a second
   * vocabulary, and it stops at this prop.
   */
  callRole: CallRole;
  /**
   * What the vendor needs to connect, or `null` while it is still being
   * fetched. MUST be referentially stable once it arrives — it is the session
   * effect's only dependency, and a new object every render would tear the
   * session down and rebuild it every render.
   */
  credential: JoinCredential | null;
  /** Shown on the surface's chrome. */
  title?: string;
  /** Screen share is capability-gated back-end; this only hides the control. */
  canShareScreen?: boolean;
  /**
   * Who is at the door. Entirely the caller's: this package neither fetches it
   * nor knows what an entry means. Pass nothing for a surface that has no door.
   */
  waiting?: readonly WaitingEntry[];
  onAdmit?: (id: string) => Promise<void> | void;
  onDecline?: (id: string) => Promise<void> | void;
  onLeave?: () => void;
  /** Element id the expanded surface is measured from. Falls back to the viewport. */
  anchorId?: string;
}

const DEVICE_TIMEOUT_MS = 10_000;

type Capture = MediaCapture<DefaultDeviceController>;

const mediaDiagnostics = createCallDiagnostics("app-media", "local");
export const mediaReadiness = createMediaReadiness(
  () => {
    const controller = new DefaultDeviceController(new ConsoleLogger(LOGGER_NAME, LogLevel.WARN));
    // SDK enumeration otherwise prompts for BOTH devices just to reveal labels.
    // Readiness must never turn a granted microphone into a camera prompt.
    controller.setDeviceLabelTrigger(() => Promise.resolve(new MediaStream()));
    return controller;
  },
  undefined,
  mediaDiagnostics.emit,
);

/** Metadata-only checks: these never acquire devices or request permission. */
export function useMediaReadiness() {
  useEffect(() => {
    const check = () => {
      void mediaReadiness.check();
    };
    const resume = () => {
      if (document.visibilityState === "visible") check();
    };
    check();
    navigator.mediaDevices?.addEventListener("devicechange", check);
    window.addEventListener("focus", check);
    document.addEventListener("visibilitychange", resume);
    return () => {
      navigator.mediaDevices?.removeEventListener("devicechange", check);
      window.removeEventListener("focus", check);
      document.removeEventListener("visibilitychange", resume);
    };
  }, []);
}

/** Ref detachment also runs during layout swaps, after a session may have died. */
function stopPreview(controller: DefaultDeviceController, element: HTMLVideoElement) {
  try {
    controller.stopVideoPreviewForVideoInput(element);
  } catch {
    // Already detached; input release must still run on hangup.
  }
}

const NO_WAITING: readonly WaitingEntry[] = [];

export function ConferencingProvider({
  children,
  roomKey,
  callRole,
  credential,
  title,
  canShareScreen = false,
  waiting,
  onAdmit,
  onDecline,
  onLeave,
  anchorId,
}: ConferencingProviderProps) {
  const [diagnostics] = useState(() => createCallDiagnostics(roomKey, callRole));
  const sessionRef = useRef<DefaultMeetingSession | null>(null);
  const captureRef = useRef<Capture | null>(null);
  const previewRef = useRef<HTMLVideoElement | null>(null);
  const [previewReady, setPreviewReady] = useState(false);
  const [deviceErrors, setDeviceErrors] = useState<{ camera?: string; microphone?: string }>({});
  const videoRefs = useRef(new Map<number, HTMLVideoElement>());
  const audioRef = useRef<HTMLAudioElement>(null);
  const stopSessionRef = useRef<(() => void) | null>(null);
  const sessionCleanupRef = useRef<(() => void) | null>(null);
  const teardownRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  /** A pending "this room has gone", cancelled if the surface comes straight back. */
  const announceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const [roster, setRoster] = useState<ReadonlyMap<string, CallParticipant>>(() => new Map());
  const [tiles, setTiles] = useState<CallTile[]>([]);
  // The host keys this provider by room, so a different call starts false.
  // Never clear on departure: losing a colleague must not restore ringing
  // chrome and take away the clinician's way out of the minimised surface.
  const [hasRemoteAttendeeJoined, setHasRemoteAttendeeJoined] = useState(false);
  const [speaking, setSpeaking] = useState<Set<string>>(() => new Set());
  const [status, setStatus] = useState<CallStatus>("connecting");
  const [muted, setMuted] = useState(false);
  const [cameraOn, setCameraOn] = useState(true);
  const [sharing, setSharing] = useState(false);
  const [admitting, setAdmitting] = useState<string | null>(null);

  const payload = credential ? (credential.payload as VendorJoinPayload) : null;

  /**
   * The store is told this room is the active one on mount, and told it has
   * gone on unmount. `enter` is idempotent for the room already active, so a
   * shell that opened the call and then rendered this provider does not get its
   * user's `minimised` choice reset underneath it.
   */
  useEffect(() => {
    // THE LEAVE IS DEFERRED A TICK, and re-entering cancels it — the same shape
    // the session teardown below uses, for the same reason.
    //
    // `enter` is idempotent for the room already active, but StrictMode's
    // enter → leave → enter is not: the middle step clears `active`, so the
    // third step no longer matches anything and builds a fresh one with the
    // DEFAULT chrome. That silently discarded the caller's `minimised: true`,
    // and a clinician ringing out got the whole window instead of the card.
    // Deferring means the spurious leave never lands, so the re-entry is a
    // genuine no-op and whatever the shell chose survives.
    if (announceRef.current) {
      clearTimeout(announceRef.current);
      announceRef.current = null;
    } else {
      enterCall(roomKey, callRole);
    }

    return () => {
      announceRef.current = setTimeout(() => {
        announceRef.current = null;
        leaveCall(roomKey);
      }, 0);
    };
  }, [roomKey, callRole]);

  /**
   * Omitted means the surface has no door at all — the patient portal's does
   * not — which is a different thing from a door nobody is at. The column, and
   * the inset it costs the stage, exist only for the first case.
   */
  const hasWaitingRoom = waiting !== undefined;
  const waitingEntries = waiting ?? NO_WAITING;

  const minimised = useSelector(callStore, (s) =>
    s.context.active?.key === roomKey ? s.context.active.minimised : false,
  );
  /**
   * A surface with no waiting room never insets the stage, however the store's
   * flag happens to be set — `waiting` omitted means "this surface has no door"
   * and the column would be an empty strip beside the video.
   */
  const asideOpen = useSelector(callStore, (s) =>
    s.context.active?.key === roomKey ? s.context.active.asideOpen : true,
  );
  const geometry = useSurfaceGeometry(minimised, anchorId, hasWaitingRoom && asideOpen);

  const bindPreview = useCallback((element: HTMLVideoElement | null) => {
    const capture = captureRef.current;
    if (previewRef.current && capture) {
      stopPreview(capture.controller, previewRef.current);
    }
    previewRef.current = element;
    if (element && capture?.previewReady && !capture.published && !capture.disposed) {
      capture.controller.startVideoPreviewForVideoInput(element);
    }
  }, []);

  useEffect(() => {
    // The controller owns capture before credentials exist. Keep it through
    // StrictMode's rehearsal, just as we keep the session, or two grabs fight
    // for the same camera and one cleanup releases the other's hardware.
    if (captureRef.current && !captureRef.current.disposed) {
      mediaReadiness.retain(captureRef.current);
      return deferCaptureTeardown;
    }
    diagnostics.emit("camera.setupStarted");
    const capture = mediaReadiness.claim();
    captureRef.current = capture;
    void capture.camera.then((ready) => {
      if (capture.disposed) return;
      setPreviewReady(ready && !capture.published);
      setCameraOn(capture.enabled);
      if (ready && previewRef.current && !capture.published) {
        capture.controller.startVideoPreviewForVideoInput(previewRef.current);
      }
      if (!ready)
        setDeviceErrors((current) => ({
          ...current,
          camera: mediaReadiness.snapshot().camera.reason,
        }));
    });

    return deferCaptureTeardown;

    function deferCaptureTeardown() {
      const target = captureRef.current;
      if (!target) return;
      mediaReadiness.deferRelease(target, () => {
        captureRef.current = null;
        stopSessionRef.current?.();
        if (previewRef.current) stopPreview(target.controller, previewRef.current);
      });
    }
  }, [diagnostics]);

  useEffect(() => {
    if (!payload) return;

    let cancelled = false;

    // React StrictMode runs effects twice in development. Tearing the session
    // down synchronously on the first cleanup releases the microphone that the
    // second mount has already claimed, and the SDK never recovers — it logs
    // "Audio input device which was active is no longer available" and sits at
    // connecting forever, with no request ever reaching the vendor. So defer the
    // teardown by a tick and let an immediate remount cancel it. Also correct
    // outside StrictMode: a genuine unmount still stops on the next tick.
    if (teardownRef.current !== null) {
      clearTimeout(teardownRef.current);
      teardownRef.current = null;
      // Reuse its original cleanup closure too: a fresh one would close over
      // the session variables below without ever initialising them.
      if (sessionRef.current) return sessionCleanupRef.current ?? undefined;
    }

    const capture = captureRef.current;
    if (!capture) return;
    const { controller: deviceController } = capture;
    const logger = new ConsoleLogger(LOGGER_NAME, LogLevel.WARN);
    const session = new DefaultMeetingSession(
      new MeetingSessionConfiguration(payload.Meeting, payload.Attendee),
      logger,
      deviceController,
    );
    sessionRef.current = session;
    const av = session.audioVideo;
    let microphoneTrack: MediaStreamTrack | undefined;
    diagnostics.emit("join.started");
    const eventObserver = { eventDidReceive: (name: string) => diagnostics.emit(name) };
    session.eventController?.addObserver(eventObserver);

    av.addObserver({
      videoTileDidUpdate: (tileState) => {
        if (!tileState.boundAttendeeId || tileState.tileId == null) return;
        setTiles((current) => {
          const next: CallTile = {
            tileId: tileState.tileId as number,
            attendeeId: tileState.boundAttendeeId as string,
            name: nameOf(tileState.boundExternalUserId),
            isContent: Boolean(tileState.isContent),
            isLocal: Boolean(tileState.localTile),
          };
          const without = current.filter((tile) => tile.tileId !== next.tileId);
          return [...without, next];
        });
      },
      videoTileWasRemoved: (tileId) => {
        videoRefs.current.delete(tileId);
        setTiles((current) => current.filter((tile) => tile.tileId !== tileId));
      },
      audioVideoDidStart: () => {
        diagnostics.emit("connected");
        if (!cancelled) setStatus("live");
      },
      audioVideoDidStop: (status) => {
        diagnostics.emit("stopped", { statusCode: status.statusCode() });
        if (!cancelled) setStatus("ended");
      },
      audioVideoDidStartConnecting: (reconnecting) => diagnostics.emit(reconnecting ? "reconnecting" : "connecting"),
      connectionDidBecomePoor: () => diagnostics.emit("connection.poor"),
      connectionDidBecomeGood: () => diagnostics.emit("connection.good"),
      connectionDidSuggestStopVideo: () => diagnostics.emit("connection.suggestStopVideo"),
      metricsDidReceive: (report) =>
        diagnostics.metrics(() => {
          const metrics: Record<string, number> = {};
          if (microphoneTrack) {
            metrics.microphoneTrackLive = Number(microphoneTrack.readyState === "live");
            metrics.microphoneTrackEnabled = Number(microphoneTrack.enabled);
            metrics.microphoneTrackMuted = Number(microphoneTrack.muted);
          }
          const playback = audioRef.current;
          if (playback) {
            metrics.playbackConnected = Number(playback.isConnected);
            metrics.playbackPaused = Number(playback.paused);
            metrics.playbackMuted = Number(playback.muted);
            metrics.playbackReadyState = playback.readyState;
          }
          for (const [key, value] of Object.entries(report.getObservableMetrics())) {
            if (typeof value === "number" && Number.isFinite(value)) metrics[key] = value;
          }
          // Numeric quality measurements only; never serialize attendee metadata.
          let streamIndex = 0;
          for (const streams of Object.values(report.getObservableVideoMetrics())) {
            for (const stream of Object.values(streams)) {
              for (const [key, value] of Object.entries(stream)) {
                if (typeof value === "number" && Number.isFinite(value)) metrics[`stream${streamIndex}.${key}`] = value;
              }
              streamIndex++;
            }
          }
          return metrics;
        }),
    });

    av.addContentShareObserver({
      contentShareDidStart: () => !cancelled && setSharing(true),
      contentShareDidStop: () => !cancelled && setSharing(false),
    });

    // Server-attributed: this is the only honest signal that the vendor actually
    // has someone's audio. A local mic meter can move while it sends silence.
    let expandedForArrival = false;
    av.realtimeSubscribeToAttendeeIdPresence((attendeeId: string, present: boolean, externalUserId?: string) => {
      if (cancelled) return;
      diagnostics.emit(present ? "attendee.present" : "attendee.left", {
        local: Number(attendeeId === payload.Attendee.AttendeeId),
      });
      setRoster((current) =>
        updateRoster(current, { attendeeId, present, name: nameOf(externalUserId) }, payload.Attendee.AttendeeId),
      );
      // A tile arrives late, or never with the camera off. Presence means the
      // attendee actually joined, regardless of video. Latch once so a later
      // reconnect cannot undo the clinician's deliberate minimise.
      if (shouldExpandForPresence(attendeeId, present, payload.Attendee.AttendeeId, expandedForArrival)) {
        expandedForArrival = true;
        setHasRemoteAttendeeJoined(true);
        expandCall();
      }
      if (!present) {
        setSpeaking((current) => {
          const next = new Set(current);
          next.delete(attendeeId);
          return next;
        });
        return;
      }
      av.realtimeSubscribeToVolumeIndicator(
        attendeeId,
        (id: string, volume: number | null, isMuted: boolean | null) => {
          setSpeaking((current) => {
            const loud = !isMuted && (volume ?? 0) > 0.03;
            if (loud === current.has(id)) return current;
            const next = new Set(current);
            if (loud) next.add(id);
            else next.delete(id);
            return next;
          });
        },
      );
    });

    if (audioRef.current) {
      void av.bindAudioElement(audioRef.current).catch(() => diagnostics.emit("playback.bindFailed"));
    }

    diagnostics.emit("microphone.setupStarted");
    const microphone = Promise.resolve(capture.microphone).then((stream) => {
      if (cancelled || capture.disposed) return;
      microphoneTrack = stream?.getAudioTracks()[0];
      if (!microphoneTrack || microphoneTrack.readyState !== "live") throw new Error("Microphone track unavailable");
      diagnostics.emit("microphone.ready");
    });
    void startWithMicrophone({
      microphone,
      timeoutMs: DEVICE_TIMEOUT_MS,
      isActive: () => !cancelled && !capture.disposed,
      onFailure: (error) => {
        av.realtimeMuteLocalAudio();
        setMuted(true);
        diagnostics.emit("microphone.setupFailed");
        setDeviceErrors((current) => ({ ...current, microphone: String(error) }));
      },
      start: () => {
        av.start();
        capture.meetingStarted = true;
        // Publish only after meeting startup. A tile started while microphone
        // setup is pending can lose its negotiation request before av.start().
        void capture.camera.then(() => {
          if (
            cancelled ||
            !publishPreparedCamera(capture, () => {
              av.startLocalVideoTile();
            })
          )
            return;
          if (previewRef.current) stopPreview(deviceController, previewRef.current);
          setPreviewReady(false);
        });
      },
    });

    stopSessionRef.current = stopSession;
    sessionCleanupRef.current = deferTeardown;
    return deferTeardown;

    function deferTeardown() {
      teardownRef.current = setTimeout(() => {
        teardownRef.current = null;
        stopSession();
      }, 0);
    }

    function stopSession() {
      // Set here, NOT eagerly: the observers registered above read this flag,
      // and the session they belong to survives a StrictMode remount. Muting
      // them the moment cleanup is scheduled left a live, connected call
      // whose audioVideoDidStart could never reach React — the call worked
      // and the UI sat on "Connecting…" forever.
      // Capture teardown may get here first; stop only once.
      if (cancelled) return;
      diagnostics.emit("leave.requested");
      session.eventController?.removeObserver(eventObserver);
      cancelled = true;
      sessionRef.current = null;
      stopSessionRef.current = null;
      try {
        av.stopLocalVideoTile();
      } catch {
        // An already-dead tile must not prevent disconnecting the session.
      }
      try {
        av.stop();
      } catch {
        // An already-dead session must not prevent releasing its devices.
      }
    }
  }, [payload, diagnostics]);

  /** Binding must happen after the element exists, so it rides the ref callback. */
  const bindTile = useCallback((tileId: number) => {
    return (element: HTMLVideoElement | null) => {
      if (!element) {
        videoRefs.current.delete(tileId);
        return;
      }
      videoRefs.current.set(tileId, element);

      sessionRef.current?.audioVideo.bindVideoElement(tileId, element);
    };
  }, []);

  useEffect(() => {
    diagnostics.emit(muted ? "microphone.muted" : "microphone.unmuted");
  }, [muted, diagnostics]);
  useEffect(() => {
    diagnostics.emit(cameraOn ? "camera.enabled" : "camera.disabled");
  }, [cameraOn, diagnostics]);
  useEffect(() => {
    diagnostics.emit(sharing ? "screenShare.started" : "screenShare.stopped");
  }, [sharing, diagnostics]);

  const toggleMute = useCallback(() => {
    const av = sessionRef.current?.audioVideo;
    if (!av) return;
    setMuted((current) => {
      if (current) av.realtimeUnmuteLocalAudio();
      else av.realtimeMuteLocalAudio();
      return !current;
    });
  }, []);

  const toggleCamera = useCallback(() => {
    const capture = captureRef.current;
    if (!capture || capture.disposed || capture.pending) return;
    const av = sessionRef.current?.audioVideo;
    const controller = capture.controller;
    const enabled = !capture.enabled;
    if (!enabled) {
      av?.stopLocalVideoTile();
      if (previewRef.current) stopPreview(controller, previewRef.current);
      setPreviewReady(false);
    }
    void mediaReadiness.setCameraEnabled(capture, enabled).then((ready) => {
      if (capture.disposed) return;
      const live = sessionRef.current?.audioVideo;
      if (live)
        publishPreparedCamera(capture, () => {
          live.startLocalVideoTile();
        });
      setPreviewReady(ready && !capture.published);
      if (ready && !capture.published && previewRef.current) {
        controller.startVideoPreviewForVideoInput(previewRef.current);
      }
      setCameraOn(capture.enabled);
      setDeviceErrors((current) => ({ ...current, camera: mediaReadiness.snapshot().camera.reason }));
    });
  }, []);

  const toggleShare = useCallback(() => {
    const av = sessionRef.current?.audioVideo;
    if (!av) return;
    void (async () => {
      try {
        if (sharing) av.stopContentShare();
        else await av.startContentShareFromScreenCapture();
      } catch {
        // Dismissing the browser's picker throws; that is a normal outcome.
      }
    })();
  }, [sharing]);

  const admit = useCallback(
    (id: string) => {
      if (!onAdmit) return;
      setAdmitting(id);
      void (async () => {
        try {
          await onAdmit(id);
        } finally {
          setAdmitting((current) => (current === id ? null : current));
        }
      })();
    },
    [onAdmit],
  );

  const decline = useCallback(
    (id: string) => {
      void onDecline?.(id);
    },
    [onDecline],
  );

  const leave = useCallback(() => {
    leaveCall(roomKey);
    onLeave?.();
  }, [roomKey, onLeave]);

  const value = useMemo<CallContextValue>(
    () => ({
      status,
      deviceErrors,
      previewReady,
      bindPreview,
      tiles,
      roster,
      hasRemoteAttendeeJoined,
      speaking,
      muted,
      cameraOn,
      sharing,
      canShareScreen,
      title,
      waiting: waitingEntries,
      hasWaitingRoom,
      admitting,
      geometry,
      actions: {
        toggleMute,
        toggleCamera,
        toggleShare,
        leave,
        minimise: minimiseCall,
        expand: expandCall,
        toggleAside: toggleCallAside,
        admit,
        decline,
      },
      bindTile,
      audioRef,
    }),
    [
      status,
      deviceErrors,
      previewReady,
      bindPreview,
      tiles,
      roster,
      hasRemoteAttendeeJoined,
      speaking,
      muted,
      cameraOn,
      sharing,
      canShareScreen,
      title,
      waitingEntries,
      hasWaitingRoom,
      admitting,
      geometry,
      toggleMute,
      toggleCamera,
      toggleShare,
      leave,
      admit,
      decline,
      bindTile,
    ],
  );

  return (
    <CallContext value={value}>
      {/*
       * FIRST, ALWAYS, AND NEVER INSIDE A VARIANT. Its position in this tree is
       * fixed, so React reconciles it in place across every chrome change and
       * the `<audio>` element it owns is never replaced.
       */}
      <CallStage />
      {children}
    </CallContext>
  );
}
